"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, MessageSquare as MessageSquareStar, Code as Code2, FileText, Search, Link2, Store, Smartphone, ChartBar as BarChart3, Target, ChartLine as LineChart, ArrowRight, CircleCheck as CheckCircle2, CalendarCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const services = [{
  id: "gbp-opt",
  title: "Google Business Profile Optimization",
  badge: "Map Pack Focus",
  description: "Complete setup and hands-on management of your Google Business Profile. I optimize primary categories, geotagged updates, and product showcases to maximize Map Pack rankings.",
  deliverable: "Enhanced local map visibility & call volume",
  iconName: "MapPin"
}, {
  id: "citation-building",
  title: "Local Citation Building & Cleanup",
  badge: "NAP Consistency",
  description: "I manually audit and build accurate Name, Address, and Phone (NAP) citations across tier-1 UK and regional directories while fixing conflicting duplicate listings.",
  deliverable: "Clean citation profile & trust signals",
  iconName: "Building2"
}, {
  id: "review-strategy",
  title: "Review Management Strategy",
  badge: "Reputation Boost",
  description: "Implementation of custom review generation workflows and response protocols. I help you build customer trust and improve sentiment signals for local algorithms.",
  deliverable: "Higher review conversion & feedback loops",
  iconName: "MessageSquareStar"
}, {
  id: "local-schema",
  title: "Local Schema & Structured Data",
  badge: "Technical SEO",
  description: "Custom JSON-LD schema markup tailored specifically for LocalBusiness, GeoCoordinates, OpeningHours, and ServiceArea schema to give search engines precise location context.",
  deliverable: "Rich snippets & search engine clarity",
  iconName: "Code2"
}, {
  id: "geo-landing-pages",
  title: "Hyper-Local Content & Geo Pages",
  badge: "Targeted Reach",
  description: "Dedicated, high-converting location pages written specifically for your target cities or towns. I avoid templated spam and create genuine, localized value.",
  deliverable: "Expanded reach across nearby towns",
  iconName: "FileText"
}, {
  id: "onpage-local",
  title: "On-Page Local Keyword Mapping",
  badge: "Intent Optimization",
  description: "I map local buyer intent keywords directly to your core service pages, refining title tags, H1-H3 headings, meta copy, and internal linking structures.",
  deliverable: "Higher rankings for commercial searches",
  iconName: "Search"
}, {
  id: "local-linkbuilding",
  title: "Local Link Building & Digital PR",
  badge: "Authority Growth",
  description: "Earning contextual backlinks through local business associations, regional press releases, community sponsorships, and relevant local partner outreach.",
  deliverable: "Authoritative regional backlink profile",
  iconName: "Link2"
}, {
  id: "multi-location",
  title: "Multi-Location SEO Strategy",
  badge: "Scalable Execution",
  description: "Tailored SEO frameworks for multi-branch regional or national businesses requiring structured location hierarchies and scalable Map Pack strategies.",
  deliverable: "Consistent multi-branch local growth",
  iconName: "Store"
}, {
  id: "mobile-voice",
  title: "Mobile & Voice Search Optimization",
  badge: "User Experience",
  description: "Optimizing your site for instant mobile loading, click-to-call conversions, and conversational voice search queries used by nearby customers on the go.",
  deliverable: "Faster mobile load & friction-free calls",
  iconName: "Smartphone"
}, {
  id: "competitor-analysis",
  title: "Local Competitor & Grid Tracking",
  badge: "Market Intelligence",
  description: "In-depth geo-grid tracking and reverse-engineering of your top 3 local competitors to uncover exact proximity, content, and backlink ranking gaps.",
  deliverable: "Actionable local competitive gap audit",
  iconName: "BarChart3"
}, {
  id: "conversion-cro",
  title: "Local CRO & Lead Capture",
  badge: "Higher Conversions",
  description: "Refining local call-to-actions, contact forms, click-to-map routes, and mobile sticky headers to convert local visitors into paying customers.",
  deliverable: "Increased phone calls & form inquiries",
  iconName: "Target"
}, {
  id: "transparent-reporting",
  title: "Local Analytics & Call Tracking",
  badge: "Clear Measurement",
  description: "Custom Looker Studio dashboards tracking phone calls, directions requests, keyword rankings, and Google Business Profile interactions with zero fluff.",
  deliverable: "100% transparent ROI dashboard",
  iconName: "LineChart"
}];
function renderServiceIcon(name: string) {
  switch (name) {
    case "MapPin":
      return <MapPin className="size-5 text-primary-foreground" />;
    case "Building2":
      return <Building2 className="size-5 text-primary-foreground" />;
    case "MessageSquareStar":
      return <MessageSquareStar className="size-5 text-primary-foreground" />;
    case "Code2":
      return <Code2 className="size-5 text-primary-foreground" />;
    case "FileText":
      return <FileText className="size-5 text-primary-foreground" />;
    case "Search":
      return <Search className="size-5 text-primary-foreground" />;
    case "Link2":
      return <Link2 className="size-5 text-primary-foreground" />;
    case "Store":
      return <Store className="size-5 text-primary-foreground" />;
    case "Smartphone":
      return <Smartphone className="size-5 text-primary-foreground" />;
    case "BarChart3":
      return <BarChart3 className="size-5 text-primary-foreground" />;
    case "Target":
      return <Target className="size-5 text-primary-foreground" />;
    case "LineChart":
      return <LineChart className="size-5 text-primary-foreground" />;
    default:
      return <MapPin className="size-5 text-primary-foreground" />;
  }
}
export default function Localseoservices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseoservicesgrid" data-nav="light" className="bg-background text-foreground py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs uppercase tracking-wider text-foreground font-semibold">
            Dedicated Local SEO Expertise
          </Badge>
          <h2 className="font-default text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Complete Local SEO Services for Sustainable Search Growth
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            When you hire me, you work directly with Yassine Essaadi—not an account manager or junior technician. Here is how I help local UK and international businesses dominate local map packs and organic search results.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:gap-8">
          {services.map((service, i) => <motion.div key={service.id} variants={itemVariants} data-index={i}>
              <Card className="group relative flex h-full flex-col overflow-hidden border border-border bg-card shadow-sm transition-all duration-300 hover:border-primary/40 hover:shadow-md">
                <div className="bg-primary px-6 py-4 flex items-center justify-between text-primary-foreground">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-lg bg-primary-foreground/10 backdrop-blur-xs">
                      {renderServiceIcon(service.iconName)}
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/90">
                      {service.badge}
                    </span>
                  </div>
                </div>

                <CardHeader className="pt-5 pb-3">
                  <CardTitle className="font-default text-xl font-bold leading-tight group-hover:text-primary transition-colors">
                    {service.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-1 pb-4">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {service.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="flex flex-col items-start gap-3 border-t border-border/60 pt-4 pb-5 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2 font-medium text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>{service.deliverable}</span>
                  </div>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 rounded-2xl bg-secondary/50 border border-border p-8 text-center sm:p-10 lg:flex lg:items-center lg:justify-between lg:text-left">
          <div className="max-w-2xl">
            <h3 className="font-default text-2xl font-semibold text-foreground">
              Need a Tailored Local SEO Strategy for Your Business?
            </h3>
            <p className="mt-2 text-sm text-muted-foreground sm:text-base">
              I provide transparent, hands-on consultancy with direct access to me throughout every campaign. No long-term lock-in contracts, just proven strategies.
            </p>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center lg:justify-end gap-3 lg:mt-0">
            <Button size="lg" asChild className="w-full sm:w-auto font-medium">
              <Link to="/ContactPage">
                <CalendarCheck className="mr-2 size-4" />
                Book a Free SEO Consultation
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="w-full sm:w-auto font-medium">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}